import React, { useState } from 'react';
import { BarChart3, TrendingUp, Sparkles, Calendar, Info } from 'lucide-react';

export interface ScanTrendItem {
  date: string;
  count: number;
}

interface ScanActivityChartProps {
  trends: ScanTrendItem[];
  totalScans: number;
}

export const ScanActivityChart: React.FC<ScanActivityChartProps> = ({ trends = [], totalScans = 0 }) => {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  // Fallback if trends is empty
  const safeTrends = trends.length > 0 ? trends : [
    { date: '2026-09-15', count: 0 },
    { date: '2026-09-16', count: 0 },
    { date: '2026-09-17', count: 0 },
    { date: '2026-09-18', count: 0 },
    { date: '2026-09-19', count: 0 },
    { date: '2026-09-20', count: 0 },
    { date: '2026-09-21', count: 0 },
  ];

  const maxVal = Math.max(...safeTrends.map((t) => t.count), 4);
  const weekTotal = safeTrends.reduce((acc, curr) => acc + curr.count, 0);

  // Find peak day
  const peakItem = safeTrends.reduce((prev, current) => (prev.count > current.count ? prev : current), safeTrends[0]);

  // Helper to format date into Day Name, Short Date, and Full Date
  const parseDateInfo = (dateStr: string) => {
    try {
      const parts = dateStr.split('-');
      let d: Date;
      if (parts.length === 3) {
        d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      } else {
        d = new Date(dateStr);
      }

      if (isNaN(d.getTime())) {
        return { dayName: dateStr.slice(0, 3), shortDate: dateStr, fullDate: dateStr, isToday: false };
      }

      const daysIndo = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
      const monthsIndo = [
        'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
        'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
      ];
      const fullDaysIndo = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];

      const dayName = daysIndo[d.getDay()];
      const fullDayName = fullDaysIndo[d.getDay()];
      const dayNum = String(d.getDate()).padStart(2, '0');
      const monthNum = String(d.getMonth() + 1).padStart(2, '0');
      const monthName = monthsIndo[d.getMonth()];
      const year = d.getFullYear();

      const today = new Date();
      const isToday =
        d.getDate() === today.getDate() &&
        d.getMonth() === today.getMonth() &&
        d.getFullYear() === today.getFullYear();

      return {
        dayName,
        shortDate: `${dayNum}/${monthNum}`,
        fullDate: `${fullDayName}, ${dayNum} ${monthName} ${year}`,
        isToday
      };
    } catch {
      return { dayName: dateStr.slice(0, 3), shortDate: dateStr, fullDate: dateStr, isToday: false };
    }
  };

  // Y-axis grid markers
  const yAxisTicks = [
    maxVal,
    Math.round((maxVal * 2) / 3),
    Math.round(maxVal / 3),
    0
  ];

  return (
    <div className="bg-white p-4 sm:p-6 rounded-2xl border border-slate-200/90 shadow-2xs space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-1">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm sm:text-base font-bold text-slate-900">
              Aktivitas Pemindaian 7 Hari Terakhir
            </h3>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
              7 Hari
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Frekuensi pembaca memindai barcode & QR code buku
          </p>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          {peakItem && peakItem.count > 0 && (
            <div className="px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-semibold flex items-center gap-1.5 shadow-2xs">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
              <span>Puncak: <strong>{peakItem.count} scan</strong></span>
            </div>
          )}
          <div className="w-8 h-8 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
            <BarChart3 className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* Chart Canvas Area */}
      <div className="relative pt-6 pb-2">
        {/* Horizontal Background Grid Lines */}
        <div className="absolute inset-x-0 top-6 bottom-10 flex flex-col justify-between pointer-events-none">
          {yAxisTicks.map((tick, i) => (
            <div key={`yaxis-tick-${tick}-${i}`} className="flex items-center gap-2 w-full">
              <span className="text-[10px] font-mono text-slate-300 w-4 text-right shrink-0">
                {tick}
              </span>
              <div className="flex-1 border-b border-dashed border-slate-100" />
            </div>
          ))}
        </div>

        {/* Bars Container */}
        <div className="relative pl-6 pr-1 h-44 sm:h-52 flex items-end justify-between gap-1.5 sm:gap-3 z-10">
          {safeTrends.map((t, idx) => {
            const { dayName, shortDate, fullDate, isToday } = parseDateInfo(t.date);
            const heightPercent = maxVal > 0 ? Math.round((t.count / maxVal) * 100) : 0;
            const isHovered = activeIdx === idx;
            const isPeak = t.count === peakItem.count && peakItem.count > 0;

            return (
              <div
                key={`trend-bar-${t.date}-${idx}`}
                className="flex-1 flex flex-col items-center h-full justify-end group cursor-pointer relative"
                onMouseEnter={() => setActiveIdx(idx)}
                onMouseLeave={() => setActiveIdx(null)}
                onClick={() => setActiveIdx(activeIdx === idx ? null : idx)}
              >
                {/* Floating Tooltip for Active / Hovered Bar */}
                {isHovered && (
                  <div className="absolute -top-12 z-30 bg-slate-900 text-white text-[11px] rounded-xl py-1.5 px-2.5 shadow-xl border border-slate-800 whitespace-nowrap flex flex-col items-center pointer-events-none animate-in fade-in zoom-in-95 duration-150">
                    <span className="font-semibold text-white">{fullDate}</span>
                    <div className="flex items-center gap-1.5 mt-0.5 text-indigo-300 font-bold">
                      <span>{t.count} kali scan</span>
                      {weekTotal > 0 && (
                        <span className="text-[10px] text-slate-400 font-normal">
                          ({Math.round((t.count / weekTotal) * 100)}%)
                        </span>
                      )}
                    </div>
                    {/* Tiny Triangle Pointer */}
                    <div className="w-2 h-2 bg-slate-900 rotate-45 -mb-1 mt-0.5" />
                  </div>
                )}

                {/* Number on Top of Bar */}
                <span
                  className={`text-[10px] sm:text-xs font-bold transition-all duration-150 mb-1.5 ${
                    isHovered
                      ? 'text-indigo-600 scale-110'
                      : isPeak
                      ? 'text-emerald-700 font-extrabold'
                      : t.count > 0
                      ? 'text-slate-700'
                      : 'text-slate-300'
                  }`}
                >
                  {t.count}
                </span>

                {/* Visual Bar Column */}
                <div className="w-full max-w-[48px] bg-slate-100 group-hover:bg-indigo-50 rounded-t-xl h-28 sm:h-36 flex items-end justify-center overflow-hidden transition-all duration-200 border border-slate-200/50 group-hover:border-indigo-300 shadow-2xs">
                  <div
                    style={{ height: `${Math.max(heightPercent, t.count > 0 ? 12 : 5)}%` }}
                    className={`w-full transition-all duration-300 rounded-t-lg ${
                      isPeak
                        ? 'bg-gradient-to-t from-emerald-600 to-teal-500 group-hover:from-emerald-700 group-hover:to-teal-600 shadow-xs'
                        : isToday
                        ? 'bg-gradient-to-t from-indigo-600 to-indigo-500 group-hover:from-indigo-700 group-hover:to-indigo-600'
                        : t.count > 0
                        ? 'bg-gradient-to-t from-indigo-500 to-indigo-400 group-hover:from-indigo-600 group-hover:to-indigo-500'
                        : 'bg-slate-200'
                    }`}
                  />
                </div>

                {/* X-Axis Date Labels */}
                <div className="mt-2 text-center flex flex-col items-center min-w-0">
                  <span
                    className={`text-[10px] sm:text-xs font-semibold leading-tight transition ${
                      isHovered
                        ? 'text-indigo-600 font-bold'
                        : isToday
                        ? 'text-indigo-600 font-bold'
                        : 'text-slate-700'
                    }`}
                  >
                    {dayName}
                  </span>
                  <span className="text-[9px] sm:text-[10px] text-slate-400 font-mono leading-tight">
                    {shortDate}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Responsive Footer Info Bar */}
      <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-slate-500">
        <div className="flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
          <span>
            Total 7 hari ini: <strong className="text-slate-900 font-semibold">{weekTotal} pemindaian</strong>
          </span>
        </div>

        <div className="flex items-center gap-2 text-[11px]">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            <span>Puncak Mingguan</span>
          </span>
          <span className="text-slate-300">•</span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-indigo-600"></span>
            <span>Reguler</span>
          </span>
        </div>
      </div>
    </div>
  );
};
